/**
 * Polls /version.json to detect when a new frontend build has been
 * deployed, then surfaces a banner the user can click to reload — JWT
 * (localStorage.token) survives location.reload() so the user stays
 * logged in.
 *
 * Why a banner instead of forced reload:
 *   - users on prod (65% WeChat 微信内置, 14% Edge, 8% Chrome, rest
 *     Safari/Android — measured 2026-06-02) sometimes have unsaved input
 *     in a drawer when a build lands; forcing a reload would lose that.
 *   - banner is non-modal and has a "本次忽略" button per session.
 *
 * Why no Service Worker / WebSocket:
 *   - WeChat X5 SW support is patchy; this app has no WS infra.
 *   - polling is robust + 5-min granularity is plenty (deploy cadence
 *     is hours/days, not seconds).
 *
 * Cache-buster (`?_t=`) + `cache: 'no-cache'`:
 *   - WeChat 微信内置 / 安卓自带浏览器 aggressively cache static JSON
 *     at the X5 layer; the query string forces a fresh fetch even when
 *     `Cache-Control: no-cache` isn't honored end-to-end.
 *
 * Mounted exactly once at App.vue scope — singleton via module-level
 * state so any future second consumer doesn't fork the timer.
 */
import {ref, onMounted, onUnmounted} from 'vue'

const VERSION_URL = '/version.json'
const POLL_INTERVAL_MS = 5 * 60 * 1000  // 5 minutes

// Module-level singletons — multiple useVersionPoll() callers share state.
const _update_available = ref(false)
const _banner_dismissed = ref(false)
let _baseline_version = null     // the version this tab booted with
// The remote version we've already shown the banner for (or dismissed for).
// When the poller sees a remote version DIFFERENT from this one, we re-arm
// both flags. Prevents the per-deploy "dismissed once → never shows again"
// bug, and the per-tick "banner re-renders on every poll" spam.
let _last_shown_version = null
let _timer_id = 0
let _started = false

// 与 router/index.js 的 isAdminUser 同源判定（sessionStorage 优先）。
// 未登录视为 normal user —— 登录页不需要被 admin-only 发版打扰。
export function isAdminAudience() {
  try {
    const sessionFlag = sessionStorage.getItem('user_is_admin')
    if (sessionFlag === 'true' || sessionFlag === 'false') return sessionFlag === 'true'
    return localStorage.getItem('user_is_admin') === 'true'
  } catch (_) {
    return false
  }
}

async function _fetch_version() {
  try {
    const resp = await fetch(`${VERSION_URL}?_t=${Date.now()}`, {
      cache: 'no-cache',
      credentials: 'same-origin',
      // Don't include any headers that would CORS-fail on the static asset.
    })
    if (!resp.ok) return null
    const data = await resp.json()
    if (!data || typeof data.v !== 'string') return null
    // audience（docs/spec/release-audience-spec.md）：缺失按 'all' 保守处理。
    return {v: data.v, audience: data.audience === 'admin' ? 'admin' : 'all'}
  } catch (_) {
    // Network down / JSON malformed / WeChat X5 quirks — just retry next tick.
    return null
  }
}

async function _tick() {
  const remote = await _fetch_version()
  if (!remote) return
  const current = remote.v
  if (_baseline_version === null) {
    _baseline_version = current
    return
  }
  if (current === _baseline_version) {
    return  // no update
  }
  // admin-only 发版：普通用户不弹横幅（也不推进 _last_shown_version，
  // 这样若同一版本后续被判定为应提示——如用户登录成为 admin——仍能弹出）。
  if (remote.audience === 'admin' && !isAdminAudience()) {
    return
  }
  // current !== _baseline_version → there's an update.
  // Only (re)arm the banner if this is a NEW remote version we haven't
  // already shown/dismissed for. Without this guard, the per-tick
  // assignment would keep _update_available pinned true, and a previous
  // dismiss() would suppress the banner forever — both broken.
  if (current !== _last_shown_version) {
    _last_shown_version = current
    _update_available.value = true
    _banner_dismissed.value = false  // re-arm for this new deploy
  }
}

function _start() {
  if (_started) return
  _started = true
  // Fire once immediately to establish baseline; then start interval.
  _tick()
  _timer_id = setInterval(_tick, POLL_INTERVAL_MS)
  // Re-check when the tab regains focus — common case: user left the
  // tab open overnight, comes back, version has long since changed.
  window.addEventListener('focus', _tick)
}

function _stop() {
  // Only called if every consumer unmounts; in practice the App.vue-
  // scoped instance is permanent for the SPA lifetime so this is a no-op.
  if (_timer_id) clearInterval(_timer_id)
  _timer_id = 0
  _started = false
  window.removeEventListener('focus', _tick)
}

export function useVersionPoll() {
  onMounted(_start)
  // No onUnmounted — we want the poller alive for the SPA lifetime.
  // Components mounting/unmounting (e.g. drawer open/close) shouldn't
  // tear down the timer.
  return {
    update_available: _update_available,
    banner_dismissed: _banner_dismissed,
    reload() {
      // location.reload() looked OK in desktop Chrome but reportedly
      // hung in WeChat 微信内置 X5: the X5 cache served the stale
      // index.html, which referenced old app.<hash>.js chunks that
      // were deleted by the new build → chunk 404 → SPA boot failed →
      // visible as "刷新按钮卡住". Same workaround the existing
      // X-App-Version handler in request/index.js uses: navigate to a
      // unique URL via a cache-busting query string so X5 has no
      // cached entry to fall back on. `replace` (not `assign`) so the
      // stale tab doesn't end up in the back-stack.
      //
      // localStorage (where JWT lives) is preserved across this
      // navigation — same origin, no session cookie touch.
      const sep = window.location.search ? '&' : '?'
      const url = window.location.pathname
                + window.location.search
                + sep + '_ac_r=' + Date.now()
      window.location.replace(url)
    },
    dismiss() {
      // Mark this specific remote version as "user has explicitly opted
      // to skip". _tick won't re-arm until it sees a DIFFERENT remote
      // version (i.e. a subsequent deploy). _baseline_version is left
      // alone so we still know what code we're actually running.
      _banner_dismissed.value = true
    },
  }
}
